/**
 * Site Settings Manager
 * Admin page to manage homepage stats, testimonials, feature cards, footer, and university marquee
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  GripVertical,
  Users,
  Globe,
  GraduationCap,
  Award,
  Sparkles,
  Star,
  Plane,
  MessageSquare,
  BarChart3,
  Building,
  Settings,
  Loader2,
  RefreshCw,
  Play,
  User,
  Upload,
  Image,
  X,
  Link2,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  MapPin,
  Flag
} from 'lucide-react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { useCMSAuth } from '@/context/CMSAuthContext';

const API_URL = window.location.origin;

// Icon options for dropdowns
const iconOptions = [
  { value: 'Users', label: 'Users' },
  { value: 'Globe', label: 'Globe' },
  { value: 'GraduationCap', label: 'Graduation Cap' },
  { value: 'Award', label: 'Award' },
  { value: 'Sparkles', label: 'Sparkles' },
  { value: 'Star', label: 'Star' },
  { value: 'Plane', label: 'Plane' },
  { value: 'Building', label: 'Building' },
  { value: 'BarChart3', label: 'Chart' },
  { value: 'MessageSquare', label: 'Message' }
];

// Footer Link Section Editor Component
const FooterLinkSection = ({ title, links, onUpdate }) => {
  const [expanded, setExpanded] = useState(false);
  
  const addLink = () => {
    onUpdate([...links, { name: 'New Link', href: '/', is_active: true }]);
  };
  
  const updateLink = (index, field, value) => {
    const updated = [...links];
    updated[index] = { ...updated[index], [field]: value };
    onUpdate(updated);
  };
  
  const removeLink = (index) => {
    onUpdate(links.filter((_, i) => i !== index));
  };
  
  const moveLink = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= links.length) return;
    const updated = [...links];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    onUpdate(updated);
  };
  
  const activeCount = links.filter(l => l.is_active !== false).length;
  
  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="font-medium text-slate-700">{title}</span>
          <span className="text-xs text-slate-500 bg-white px-2 py-0.5 rounded-full">
            {activeCount} of {links.length} active
          </span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      
      {expanded && (
        <div className="p-3 space-y-2 bg-white">
          {links.map((link, idx) => (
            <div 
              key={idx} 
              className={`flex items-center gap-2 p-2 rounded border ${link.is_active === false ? 'bg-slate-50 opacity-60' : 'bg-white'}`}
            >
              <div className="flex flex-col gap-1">
                <button 
                  type="button"
                  onClick={() => moveLink(idx, 'up')} 
                  disabled={idx === 0}
                  className="p-0.5 hover:bg-slate-100 rounded disabled:opacity-30"
                >
                  <ChevronUp className="w-3 h-3" />
                </button>
                <button 
                  type="button"
                  onClick={() => moveLink(idx, 'down')} 
                  disabled={idx === links.length - 1}
                  className="p-0.5 hover:bg-slate-100 rounded disabled:opacity-30"
                >
                  <ChevronDown className="w-3 h-3" />
                </button>
              </div>
              
              <Input
                value={link.name}
                onChange={(e) => updateLink(idx, 'name', e.target.value)}
                placeholder="Link name"
                className="flex-1 h-8 text-sm"
              />
              
              <Input
                value={link.href}
                onChange={(e) => updateLink(idx, 'href', e.target.value)}
                placeholder="/path"
                className="flex-1 h-8 text-sm font-mono"
              />
              
              <button
                type="button"
                onClick={() => updateLink(idx, 'is_active', link.is_active === false ? true : false)}
                className={`p-1.5 rounded transition-colors ${link.is_active === false ? 'text-slate-400 hover:text-slate-600' : 'text-emerald-500 hover:text-emerald-600'}`}
                title={link.is_active === false ? 'Show link' : 'Hide link'}
              >
                {link.is_active === false ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              
              <button
                type="button"
                onClick={() => removeLink(idx)}
                className="p-1.5 text-red-400 hover:text-red-600 rounded transition-colors"
                title="Remove link"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          
          <button
            type="button"
            onClick={addLink}
            className="w-full flex items-center justify-center gap-1 p-2 text-sm text-ux-cyan hover:bg-cyan-50 rounded border border-dashed border-cyan-300 transition-colors"
          >
            <Plus className="w-4 h-4" /> Add Link
          </button>
        </div>
      )}
    </div>
  );
};

const SiteSettingsManager = () => {
  const navigate = useNavigate();
  const { token, logout } = useCMSAuth();
  const [activeTab, setActiveTab] = useState('stats');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Data states
  const [stats, setStats] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [featureCards, setFeatureCards] = useState([]);
  const [marqueeUniversities, setMarqueeUniversities] = useState([]);
  const [footerSettings, setFooterSettings] = useState({});
  const [socialProof, setSocialProof] = useState({
    rating: 4.9,
    total_reviews: "1,000+",
    review_text: "verified reviews",
    avatar_images: [],
    is_active: true
  });
  const [videoSettings, setVideoSettings] = useState({
    is_enabled: true,
    video_type: 'youtube',
    video_url: '',
    video_id: '',
    thumbnail_url: '',
    title: 'Discover Your Study Abroad Journey',
    subtitle: 'Watch how UniXperts helps students achieve their dreams'
  });
  
  // Asset picker state for testimonial images
  const [assetPickerOpen, setAssetPickerOpen] = useState(false);
  const [assetPickerIndex, setAssetPickerIndex] = useState(null);
  const [availableAssets, setAvailableAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Navigation settings state
  const [navigationSettings, setNavigationSettings] = useState({
    destinations: [],
    services: [],
    student_services: [],
    programs: [],
    comparisons: []
  });
  
  // Journey settings state
  const [journeySettings, setJourneySettings] = useState({
    personas: []
  });
  
  // Success stories settings state
  const [successStoriesSettings, setSuccessStoriesSettings] = useState({
    stories: [],
    stats: [],
    filter_countries: [],
    filter_programs: []
  });

  // Helper for 401 handling
  const handleUnauthorized = (res) => {
    if (res && res.status === 401) {
      toast.error('Session expired. Please log in again.');
      logout();
      return true;
    }
    return false;
  };

  // Fetch all data on mount
  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, testimonialsRes, cardsRes, marqueeRes, footerRes, videoRes, socialRes, navRes, journeyRes, storiesRes] = await Promise.all([
        fetch(`${API_URL}/api/cms/site-settings/homepage-stats`),
        fetch(`${API_URL}/api/cms/site-settings/testimonials`),
        fetch(`${API_URL}/api/cms/site-settings/feature-cards`),
        fetch(`${API_URL}/api/cms/site-settings/university-marquee`),
        fetch(`${API_URL}/api/cms/site-settings/footer`),
        fetch(`${API_URL}/api/cms/site-settings/video`),
        fetch(`${API_URL}/api/cms/site-settings/social-proof`),
        fetch(`${API_URL}/api/cms/site-settings/navigation`),
        fetch(`${API_URL}/api/cms/site-settings/journey`),
        fetch(`${API_URL}/api/cms/site-settings/success-stories`)
      ]);

      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data.stats || []);
      }
      if (testimonialsRes.ok) {
        const data = await testimonialsRes.json();
        setTestimonials(data.testimonials || []);
      }
      if (cardsRes.ok) {
        const data = await cardsRes.json();
        setFeatureCards(data.cards || []);
      }
      if (marqueeRes.ok) {
        const data = await marqueeRes.json();
        setMarqueeUniversities(data.universities || []);
      }
      if (footerRes.ok) {
        const data = await footerRes.json();
        setFooterSettings(data);
      }
      if (videoRes.ok) {
        const data = await videoRes.json();
        setVideoSettings(data);
      }
      if (socialRes.ok) {
        const data = await socialRes.json();
        setSocialProof(data);
      }
      if (navRes.ok) {
        const data = await navRes.json();
        setNavigationSettings(data);
      }
      if (journeyRes.ok) {
        const data = await journeyRes.json();
        setJourneySettings(data);
      }
      if (storiesRes.ok) {
        const data = await storiesRes.json();
        setSuccessStoriesSettings(data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  // Fetch available assets for the image picker
  const fetchAvailableAssets = async () => {
    setAssetsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/assets/?active_only=true`);
      if (res.ok) {
        const data = await res.json();
        // Filter to only image-type assets and deduplicate by URL
        const seen = new Set();
        const images = (data.assets || []).filter(a => {
          const isImage = a.type === 'image' || a.url?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
          const url = a.url || '';
          if (!isImage || !url || seen.has(url)) return false;
          seen.add(url);
          return true;
        });
        setAvailableAssets(images);
      }
    } catch (error) {
      console.error('Error fetching assets:', error);
    } finally {
      setAssetsLoading(false);
    }
  };

  // Handle opening asset picker for a specific testimonial
  const openAssetPicker = (index) => {
    setAssetPickerIndex(index);
    setAssetPickerOpen(true);
    fetchAvailableAssets();
  };

  // Handle selecting an asset from the picker
  const handleAssetSelect = (assetUrl) => {
    if (assetPickerIndex !== null) {
      const newT = [...testimonials];
      newT[assetPickerIndex].image_url = assetUrl;
      setTestimonials(newT);
    }
    setAssetPickerOpen(false);
    setAssetPickerIndex(null);
  };

  // Handle direct image upload for testimonial
  const handleTestimonialImageUpload = async (file, index) => {
    if (!file) return;
    
    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', `Testimonial - ${testimonials[index]?.name || 'Student'}`);
    formData.append('asset_key', `testimonial_${Date.now()}`);
    formData.append('type', 'image');
    formData.append('category', 'general');
    formData.append('description', 'Testimonial student photo');
    
    try {
      const res = await fetch(`${API_URL}/api/assets/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        const newT = [...testimonials];
        newT[index].image_url = data.url;
        setTestimonials(newT);
        toast.success('Image uploaded successfully!');
      } else {
        toast.error('Failed to upload image');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  // Save handlers
  const saveStats = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/cms/site-settings/homepage-stats`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ stats })
      });
      if (res.ok) {
        toast.success('Homepage stats saved!');
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save stats');
    } finally {
      setSaving(false);
    }
  };

  const saveTestimonials = async () => {
    setSaving(true);
    try {
      // Ensure required fields are present for all testimonials
      for (const t of testimonials) {
        if (!t.name || !t.university || !t.program || !t.quote) {
          toast.error(`Please fill all required fields for testimonial: ${t.name || 'New Testimonial'}`);
          setSaving(false);
          return;
        }
      }
      
      // Get existing testimonials from DB
      const existingRes = await fetch(`${API_URL}/api/cms/site-settings/testimonials/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      let existingIds = new Set();
      if (existingRes.ok) {
        const existingData = await existingRes.json();
        const existingTestimonials = existingData.testimonials || [];
        existingIds = new Set(existingTestimonials.map(t => t.id));
      }
      
      // Track which IDs we're keeping
      const currentIds = new Set();
      
      // Save each testimonial - update if exists, create if new
      for (let i = 0; i < testimonials.length; i++) {
        const t = testimonials[i];
        const testimonialData = {
          name: t.name,
          university: t.university,
          program: t.program,
          quote: t.quote,
          rating: t.rating || 5,
          image_url: t.image_url || null,
          image_key: t.image_key || null,
          country: t.country || '',
          year: t.year || new Date().getFullYear().toString(),
          order: i,
          is_active: true
        };
        
        if (t.id && existingIds.has(t.id)) {
          // Update existing
          currentIds.add(t.id);
          const updateRes = await fetch(`${API_URL}/api/cms/site-settings/testimonials/${t.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(testimonialData)
          });
          
          if (!updateRes.ok) {
            const errorText = await updateRes.text();
            console.error('Update failed:', errorText);
            throw new Error(`Update failed: ${errorText}`);
          }
        } else {
          // Create new
          const createRes = await fetch(`${API_URL}/api/cms/site-settings/testimonials`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(testimonialData)
          });
          
          if (!createRes.ok) {
            const errorText = await createRes.text();
            console.error('Create failed:', errorText);
            throw new Error(`Create failed: ${errorText}`);
          }
        }
      }
      
      // Delete testimonials that were removed from UI
      for (const existingId of existingIds) {
        if (!currentIds.has(existingId)) {
          await fetch(`${API_URL}/api/cms/site-settings/testimonials/${existingId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });
        }
      }
      
      toast.success('Testimonials saved!');
      fetchAllData();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(`Failed to save testimonials: ${error.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const saveFeatureCards = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/cms/site-settings/feature-cards`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(featureCards)
      });
      if (res.ok) {
        toast.success('Feature cards saved!');
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save feature cards');
    } finally {
      setSaving(false);
    }
  };

  const saveMarquee = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/cms/site-settings/university-marquee`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(marqueeUniversities)
      });
      if (res.ok) {
        toast.success('University marquee saved!');
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save marquee');
    } finally {
      setSaving(false);
    }
  };

  const saveFooter = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/cms/site-settings/footer`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(footerSettings)
      });
      if (res.ok) {
        toast.success('Footer settings saved!');
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save footer');
    } finally {
      setSaving(false);
    }
  };

  const saveVideoSettings = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/cms/site-settings/video`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(videoSettings)
      });
      if (res.ok) {
        const data = await res.json();
        toast.success('Video settings saved!');
        // Update local state with extracted video ID
        if (data.video_id) {
          setVideoSettings(prev => ({ ...prev, video_id: data.video_id }));
        }
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save video settings');
    } finally {
      setSaving(false);
    }
  };

  // Helper functions
  const addStat = () => {
    setStats([...stats, {
      id: `stat_${Date.now()}`,
      label: 'New Stat',
      value: '0',
      suffix: '+',
      icon: 'Star',
      order: stats.length,
      is_active: true
    }]);
  };

  const addTestimonial = () => {
    setTestimonials([...testimonials, {
      id: '',
      name: '',
      university: '',
      program: '',
      quote: '',
      rating: 5,
      country: '',
      year: new Date().getFullYear().toString(),
      order: testimonials.length,
      is_active: true
    }]);
  };

  const addFeatureCard = () => {
    setFeatureCards([...featureCards, {
      id: `card_${Date.now()}`,
      title: 'New Feature',
      description: '',
      icon: 'Star',
      gradient: 'from-ux-cyan to-ux-blue',
      link: '/',
      order: featureCards.length,
      is_active: true
    }]);
  };

  const addMarqueeUniversity = () => {
    setMarqueeUniversities([...marqueeUniversities, {
      id: `uni_${Date.now()}`,
      name: 'University Name',
      order: marqueeUniversities.length,
      is_active: true
    }]);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
          <Loader2 className="w-8 h-8 animate-spin text-ux-cyan" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 lg:px-8 py-4 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-slate-800">Homepage & Footer</h1>
              <p className="text-sm text-slate-500">Manage homepage content, stats, testimonials, and footer</p>
            </div>
          </div>
          <Button variant="outline" onClick={fetchAllData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-4 lg:p-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-6 mb-6">
            <TabsTrigger value="stats">
              <BarChart3 className="w-4 h-4 mr-2" />
              Stats
            </TabsTrigger>
            <TabsTrigger value="testimonials">
              <MessageSquare className="w-4 h-4 mr-2" />
              Testimonials
            </TabsTrigger>
            <TabsTrigger value="video">
              <Play className="w-4 h-4 mr-2" />
              Video
            </TabsTrigger>
            <TabsTrigger value="features">
              <Sparkles className="w-4 h-4 mr-2" />
              Features
            </TabsTrigger>
            <TabsTrigger value="marquee">
              <Building className="w-4 h-4 mr-2" />
              Marquee
            </TabsTrigger>
            <TabsTrigger value="footer">
              <Settings className="w-4 h-4 mr-2" />
              Footer
            </TabsTrigger>
            <TabsTrigger value="social-proof">
              <Star className="w-4 h-4 mr-2" />
              Social Proof
            </TabsTrigger>
            <TabsTrigger value="navigation">
              <Link2 className="w-4 h-4 mr-2" />
              Navigation
            </TabsTrigger>
            <TabsTrigger value="journey">
              <Plane className="w-4 h-4 mr-2" />
              Journey
            </TabsTrigger>
            <TabsTrigger value="success-stories">
              <Award className="w-4 h-4 mr-2" />
              Success Stories
            </TabsTrigger>
          </TabsList>

          {/* Homepage Stats Tab */}
          <TabsContent value="stats">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Homepage Statistics</CardTitle>
                  <CardDescription>Edit the stats displayed on the homepage hero section</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={addStat}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Stat
                  </Button>
                  <Button onClick={saveStats} disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {stats.map((stat, index) => (
                    <div key={stat.id || index} className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                      <GripVertical className="w-5 h-5 text-slate-400 cursor-move" />
                      <div className="grid grid-cols-5 gap-4 flex-1">
                        <div>
                          <Label className="text-xs">Label</Label>
                          <Input
                            value={stat.label}
                            onChange={(e) => {
                              const newStats = [...stats];
                              newStats[index].label = e.target.value;
                              setStats(newStats);
                            }}
                            placeholder="e.g., Students Placed"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Value</Label>
                          <Input
                            value={stat.value}
                            onChange={(e) => {
                              const newStats = [...stats];
                              newStats[index].value = e.target.value;
                              setStats(newStats);
                            }}
                            placeholder="e.g., 13249"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Suffix</Label>
                          <Input
                            value={stat.suffix}
                            onChange={(e) => {
                              const newStats = [...stats];
                              newStats[index].suffix = e.target.value;
                              setStats(newStats);
                            }}
                            placeholder="e.g., + or %"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Icon</Label>
                          <select
                            value={stat.icon}
                            onChange={(e) => {
                              const newStats = [...stats];
                              newStats[index].icon = e.target.value;
                              setStats(newStats);
                            }}
                            className="w-full h-10 px-3 border border-slate-200 rounded-md text-sm"
                          >
                            {iconOptions.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="flex items-end gap-2">
                          <Switch
                            checked={stat.is_active}
                            onCheckedChange={(checked) => {
                              const newStats = [...stats];
                              newStats[index].is_active = checked;
                              setStats(newStats);
                            }}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500 hover:text-red-600"
                            onClick={() => setStats(stats.filter((_, i) => i !== index))}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Testimonials Tab */}
          <TabsContent value="testimonials">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Student Testimonials</CardTitle>
                  <CardDescription>Edit testimonials shown on the homepage</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={addTestimonial}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Testimonial
                  </Button>
                  <Button onClick={saveTestimonials} disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {testimonials.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <MessageSquare className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p className="text-lg font-medium">No testimonials yet</p>
                      <p className="text-sm mt-1">Click "Add Testimonial" to create your first student success story</p>
                    </div>
                  ) : (
                  testimonials.map((t, index) => (
                    <div key={t.id || index} className="p-4 bg-slate-50 rounded-lg space-y-4">
                      <div className="flex justify-between items-start">
                        <h4 className="font-medium">Testimonial #{index + 1}</h4>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-500"
                          onClick={() => setTestimonials(testimonials.filter((_, i) => i !== index))}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label>Name</Label>
                          <Input
                            value={t.name}
                            onChange={(e) => {
                              const newT = [...testimonials];
                              newT[index].name = e.target.value;
                              setTestimonials(newT);
                            }}
                            placeholder="Student name"
                          />
                        </div>
                        <div>
                          <Label>University</Label>
                          <Input
                            value={t.university}
                            onChange={(e) => {
                              const newT = [...testimonials];
                              newT[index].university = e.target.value;
                              setTestimonials(newT);
                            }}
                            placeholder="University name"
                          />
                        </div>
                        <div>
                          <Label>Program</Label>
                          <Input
                            value={t.program}
                            onChange={(e) => {
                              const newT = [...testimonials];
                              newT[index].program = e.target.value;
                              setTestimonials(newT);
                            }}
                            placeholder="e.g., MS Data Science"
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Quote</Label>
                        <Textarea
                          value={t.quote}
                          onChange={(e) => {
                            const newT = [...testimonials];
                            newT[index].quote = e.target.value;
                            setTestimonials(newT);
                          }}
                          placeholder="Student testimonial..."
                          rows={3}
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label>Country</Label>
                          <Input
                            value={t.country}
                            onChange={(e) => {
                              const newT = [...testimonials];
                              newT[index].country = e.target.value;
                              setTestimonials(newT);
                            }}
                            placeholder="e.g., Australia"
                          />
                        </div>
                        <div>
                          <Label>Year</Label>
                          <Input
                            value={t.year}
                            onChange={(e) => {
                              const newT = [...testimonials];
                              newT[index].year = e.target.value;
                              setTestimonials(newT);
                            }}
                            placeholder="e.g., 2024"
                          />
                        </div>
                        <div>
                          <Label>Rating (1-5)</Label>
                          <Input
                            type="number"
                            min="1"
                            max="5"
                            value={t.rating}
                            onChange={(e) => {
                              const newT = [...testimonials];
                              newT[index].rating = parseInt(e.target.value) || 5;
                              setTestimonials(newT);
                            }}
                          />
                        </div>
                      </div>
                      
                      {/* Student Photo Section - Enhanced */}
                      <div className="pt-4 border-t border-slate-200">
                        <Label className="text-sm font-medium mb-3 block">Student Photo</Label>
                        <div className="flex items-start gap-4">
                          {/* Image Preview */}
                          <div className="flex-shrink-0 relative group">
                            {t.image_url ? (
                              <div className="relative">
                                <img 
                                  src={t.image_url} 
                                  alt={t.name || 'Preview'}
                                  className="w-20 h-20 rounded-full object-cover border-2 border-slate-200 shadow-sm"
                                  onError={(e) => { e.target.src = `https://i.pravatar.cc/100?u=${t.name || index}`; }}
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newT = [...testimonials];
                                    newT[index].image_url = '';
                                    setTestimonials(newT);
                                  }}
                                  className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="Remove image"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ) : (
                              <div className="w-20 h-20 rounded-full bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400">
                                <User className="w-8 h-8" />
                              </div>
                            )}
                          </div>
                          
                          {/* Image Actions */}
                          <div className="flex-1 space-y-3">
                            <div className="flex flex-wrap gap-2">
                              {/* Browse CMS Assets Button */}
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => openAssetPicker(index)}
                                className="text-slate-600"
                                data-testid={`browse-assets-btn-${index}`}
                              >
                                <Image className="w-4 h-4 mr-2" />
                                Browse Assets
                              </Button>
                              
                              {/* Upload New Image Button */}
                              <label className="cursor-pointer">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="text-slate-600"
                                  onClick={() => document.getElementById(`upload-testimonial-${index}`).click()}
                                  disabled={uploadingImage}
                                  data-testid={`upload-image-btn-${index}`}
                                >
                                  {uploadingImage ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  ) : (
                                    <Upload className="w-4 h-4 mr-2" />
                                  )}
                                  Upload New
                                </Button>
                                <input
                                  id={`upload-testimonial-${index}`}
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleTestimonialImageUpload(file, index);
                                    e.target.value = '';
                                  }}
                                />
                              </label>
                            </div>
                            
                            {/* URL Input (for advanced users) */}
                            <div>
                              <Input
                                value={t.image_url || ''}
                                onChange={(e) => {
                                  const newT = [...testimonials];
                                  newT[index].image_url = e.target.value;
                                  setTestimonials(newT);
                                }}
                                placeholder="Or paste image URL directly"
                                className="text-sm"
                                data-testid={`image-url-input-${index}`}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Video Section Tab */}
          <TabsContent value="video">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Homepage Video Section</CardTitle>
                  <CardDescription>Configure the video displayed next to testimonials</CardDescription>
                </div>
                <Button onClick={saveVideoSettings} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save
                </Button>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable/Disable */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label className="font-medium">Enable Video Section</Label>
                    <p className="text-sm text-slate-500">Show video section on homepage</p>
                  </div>
                  <Switch
                    checked={videoSettings.is_enabled}
                    onCheckedChange={(checked) => setVideoSettings({...videoSettings, is_enabled: checked})}
                  />
                </div>

                {videoSettings.is_enabled && (
                  <>
                    {/* Video Type */}
                    <div className="space-y-2">
                      <Label>Video Type</Label>
                      <Select 
                        value={videoSettings.video_type} 
                        onValueChange={(value) => setVideoSettings({...videoSettings, video_type: value, video_id: ''})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select video type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="youtube">YouTube</SelectItem>
                          <SelectItem value="vimeo">Vimeo</SelectItem>
                          <SelectItem value="custom">Custom Video URL</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Video URL */}
                    <div className="space-y-2">
                      <Label>
                        {videoSettings.video_type === 'youtube' ? 'YouTube URL' : 
                         videoSettings.video_type === 'vimeo' ? 'Vimeo URL' : 'Video URL'}
                      </Label>
                      <Input
                        value={videoSettings.video_url || ''}
                        onChange={(e) => setVideoSettings({...videoSettings, video_url: e.target.value})}
                        placeholder={
                          videoSettings.video_type === 'youtube' 
                            ? 'https://www.youtube.com/watch?v=...' 
                            : videoSettings.video_type === 'vimeo'
                            ? 'https://vimeo.com/...'
                            : 'https://example.com/video.mp4'
                        }
                      />
                      {videoSettings.video_id && (
                        <p className="text-sm text-green-600">Video ID detected: {videoSettings.video_id}</p>
                      )}
                    </div>

                    {/* Thumbnail URL */}
                    <div className="space-y-2">
                      <Label>Custom Thumbnail URL (optional)</Label>
                      <Input
                        value={videoSettings.thumbnail_url || ''}
                        onChange={(e) => setVideoSettings({...videoSettings, thumbnail_url: e.target.value})}
                        placeholder="https://example.com/thumbnail.jpg"
                      />
                      <p className="text-xs text-slate-500">Leave empty to use the video's default thumbnail</p>
                    </div>

                    {/* Title & Subtitle */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Title</Label>
                        <Input
                          value={videoSettings.title || ''}
                          onChange={(e) => setVideoSettings({...videoSettings, title: e.target.value})}
                          placeholder="Video title"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Subtitle</Label>
                        <Input
                          value={videoSettings.subtitle || ''}
                          onChange={(e) => setVideoSettings({...videoSettings, subtitle: e.target.value})}
                          placeholder="Video subtitle"
                        />
                      </div>
                    </div>

                    {/* Preview */}
                    <div className="space-y-2">
                      <Label>Preview</Label>
                      <div className="relative h-64 bg-slate-900 rounded-lg overflow-hidden">
                        {videoSettings.video_id && videoSettings.video_type === 'youtube' ? (
                          <iframe
                            className="absolute inset-0 w-full h-full"
                            src={`https://www.youtube.com/embed/${videoSettings.video_id}`}
                            title="Video preview"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        ) : videoSettings.video_id && videoSettings.video_type === 'vimeo' ? (
                          <iframe
                            className="absolute inset-0 w-full h-full"
                            src={`https://player.vimeo.com/video/${videoSettings.video_id}`}
                            title="Video preview"
                            frameBorder="0"
                            allow="autoplay; fullscreen; picture-in-picture"
                            allowFullScreen
                          />
                        ) : videoSettings.thumbnail_url ? (
                          <img 
                            src={videoSettings.thumbnail_url} 
                            alt="Thumbnail preview" 
                            className="absolute inset-0 w-full h-full object-cover"
                          />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center text-white/50">
                            <div className="text-center">
                              <Play className="w-12 h-12 mx-auto mb-2" />
                              <p>Enter a video URL to see preview</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Feature Cards Tab */}
          <TabsContent value="features">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Homepage Feature Cards</CardTitle>
                  <CardDescription>Edit the feature cards shown on homepage</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={addFeatureCard}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Card
                  </Button>
                  <Button onClick={saveFeatureCards} disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  {featureCards.map((card, index) => (
                    <div key={card.id || index} className="p-4 bg-slate-50 rounded-lg space-y-3">
                      <div className="flex justify-between">
                        <h4 className="font-medium">Card #{index + 1}</h4>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-500"
                          onClick={() => setFeatureCards(featureCards.filter((_, i) => i !== index))}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div>
                        <Label>Title</Label>
                        <Input
                          value={card.title}
                          onChange={(e) => {
                            const newCards = [...featureCards];
                            newCards[index].title = e.target.value;
                            setFeatureCards(newCards);
                          }}
                        />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Textarea
                          value={card.description}
                          onChange={(e) => {
                            const newCards = [...featureCards];
                            newCards[index].description = e.target.value;
                            setFeatureCards(newCards);
                          }}
                          rows={2}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>Icon</Label>
                          <select
                            value={card.icon}
                            onChange={(e) => {
                              const newCards = [...featureCards];
                              newCards[index].icon = e.target.value;
                              setFeatureCards(newCards);
                            }}
                            className="w-full h-10 px-3 border border-slate-200 rounded-md text-sm"
                          >
                            {iconOptions.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label>Link</Label>
                          <Input
                            value={card.link || ''}
                            onChange={(e) => {
                              const newCards = [...featureCards];
                              newCards[index].link = e.target.value;
                              setFeatureCards(newCards);
                            }}
                            placeholder="/services"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* University Marquee Tab */}
          <TabsContent value="marquee">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>University Marquee</CardTitle>
                  <CardDescription>Edit the scrolling university names on homepage</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={addMarqueeUniversity}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add University
                  </Button>
                  <Button onClick={saveMarquee} disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-3">
                  {marqueeUniversities.map((uni, index) => (
                    <div key={uni.id || index} className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                      <GripVertical className="w-4 h-4 text-slate-400" />
                      <Input
                        value={uni.name}
                        onChange={(e) => {
                          const newUnis = [...marqueeUniversities];
                          newUnis[index].name = e.target.value;
                          setMarqueeUniversities(newUnis);
                        }}
                        placeholder="University name"
                        className="flex-1"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500"
                        onClick={() => setMarqueeUniversities(marqueeUniversities.filter((_, i) => i !== index))}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Footer Settings Tab */}
          <TabsContent value="footer">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Footer Settings</CardTitle>
                  <CardDescription>Edit footer contact info and social links</CardDescription>
                </div>
                <Button onClick={saveFooter} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save
                </Button>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Company Name</Label>
                    <Input
                      value={footerSettings.company_name || ''}
                      onChange={(e) => setFooterSettings({ ...footerSettings, company_name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Tagline</Label>
                    <Input
                      value={footerSettings.tagline || ''}
                      onChange={(e) => setFooterSettings({ ...footerSettings, tagline: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Contact Email</Label>
                    <Input
                      value={footerSettings.contact_email || ''}
                      onChange={(e) => setFooterSettings({ ...footerSettings, contact_email: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Contact Phone</Label>
                    <Input
                      value={footerSettings.contact_phone || ''}
                      onChange={(e) => setFooterSettings({ ...footerSettings, contact_phone: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>WhatsApp Number</Label>
                    <Input
                      value={footerSettings.whatsapp_number || ''}
                      onChange={(e) => setFooterSettings({ ...footerSettings, whatsapp_number: e.target.value })}
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="mb-2 block">Social Links</Label>
                  <div className="grid grid-cols-2 gap-4">
                    {['facebook', 'instagram', 'linkedin', 'youtube', 'twitter'].map((platform) => {
                      const link = (footerSettings.social_links || []).find(l => l.platform === platform) || { platform, url: '', is_active: true };
                      return (
                        <div key={platform} className="flex items-center gap-2">
                          <span className="w-24 capitalize text-sm font-medium">{platform}</span>
                          <Input
                            value={link.url}
                            onChange={(e) => {
                              const links = [...(footerSettings.social_links || [])];
                              const idx = links.findIndex(l => l.platform === platform);
                              if (idx >= 0) {
                                links[idx].url = e.target.value;
                              } else {
                                links.push({ platform, url: e.target.value, is_active: true });
                              }
                              setFooterSettings({ ...footerSettings, social_links: links });
                            }}
                            placeholder={`https://${platform}.com/...`}
                            className="flex-1"
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <Label>Copyright Text</Label>
                  <Input
                    value={footerSettings.copyright_text || ''}
                    onChange={(e) => setFooterSettings({ ...footerSettings, copyright_text: e.target.value })}
                    placeholder="© 2024 UniXperts. All rights reserved."
                  />
                </div>
                
                {/* Footer Navigation Links Editor */}
                <div className="border-t pt-6 mt-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Link2 className="w-5 h-5 text-ux-cyan" />
                    Footer Navigation Links
                  </h3>
                  <p className="text-sm text-slate-500 mb-4">
                    Edit the navigation links displayed in the footer. Toggle links on/off or reorder them.
                  </p>
                  
                  {/* Link Sections */}
                  <div className="space-y-6">
                    {/* Destinations Links */}
                    <FooterLinkSection
                      title="Destinations"
                      links={footerSettings.destinations_links || []}
                      onUpdate={(links) => setFooterSettings({ ...footerSettings, destinations_links: links })}
                    />
                    
                    {/* Services Links */}
                    <FooterLinkSection
                      title="Services"
                      links={footerSettings.services_links || []}
                      onUpdate={(links) => setFooterSettings({ ...footerSettings, services_links: links })}
                    />
                    
                    {/* Company Links */}
                    <FooterLinkSection
                      title="Company"
                      links={footerSettings.company_links || []}
                      onUpdate={(links) => setFooterSettings({ ...footerSettings, company_links: links })}
                    />
                    
                    {/* Popular Programs Links */}
                    <FooterLinkSection
                      title="Popular Programs"
                      links={footerSettings.programs_links || []}
                      onUpdate={(links) => setFooterSettings({ ...footerSettings, programs_links: links })}
                    />
                    
                    {/* Comparisons Links */}
                    <FooterLinkSection
                      title="Comparisons"
                      links={footerSettings.comparisons_links || []}
                      onUpdate={(links) => setFooterSettings({ ...footerSettings, comparisons_links: links })}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Social Proof Tab */}
          <TabsContent value="social-proof">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Hero Social Proof</CardTitle>
                  <CardDescription>Edit the rating, reviews count, and avatar images shown on the hero section</CardDescription>
                </div>
                <Button 
                  className="bg-ux-cyan hover:bg-ux-cyan-dark"
                  onClick={async () => {
                    setSaving(true);
                    try {
                      const res = await fetch(`${API_URL}/api/cms/site-settings/social-proof`, {
                        method: 'PUT',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify(socialProof)
                      });
                      if (res.ok) {
                        toast.success('Social proof settings saved');
                      } else {
                        toast.error('Failed to save settings');
                      }
                    } catch (e) {
                      toast.error('Failed to save settings');
                    } finally {
                      setSaving(false);
                    }
                  }}
                  disabled={saving}
                >
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Rating */}
                  <div className="space-y-2">
                    <Label>Rating (0-5)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="5"
                      step="0.1"
                      value={socialProof.rating}
                      onChange={(e) => setSocialProof({...socialProof, rating: parseFloat(e.target.value) || 0})}
                      data-testid="social-proof-rating"
                    />
                    <p className="text-xs text-gray-500">Star rating displayed (e.g., 4.9)</p>
                  </div>

                  {/* Total Reviews */}
                  <div className="space-y-2">
                    <Label>Total Reviews</Label>
                    <Input
                      value={socialProof.total_reviews}
                      onChange={(e) => setSocialProof({...socialProof, total_reviews: e.target.value})}
                      placeholder="e.g., 1,000+"
                      data-testid="social-proof-reviews"
                    />
                    <p className="text-xs text-gray-500">Number displayed (e.g., "1,000+", "500+")</p>
                  </div>

                  {/* Review Text */}
                  <div className="space-y-2">
                    <Label>Review Text</Label>
                    <Input
                      value={socialProof.review_text}
                      onChange={(e) => setSocialProof({...socialProof, review_text: e.target.value})}
                      placeholder="e.g., verified reviews"
                    />
                    <p className="text-xs text-gray-500">Text after the number (e.g., "verified reviews")</p>
                  </div>

                  {/* Active Toggle */}
                  <div className="space-y-2">
                    <Label>Show Section</Label>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={socialProof.is_active}
                        onCheckedChange={(checked) => setSocialProof({...socialProof, is_active: checked})}
                      />
                      <span className="text-sm">{socialProof.is_active ? 'Visible' : 'Hidden'}</span>
                    </div>
                  </div>
                </div>

                {/* Avatar Images */}
                <div className="mt-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Avatar Images (up to 5)</Label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const newImages = [...(socialProof.avatar_images || [])];
                        if (newImages.length < 5) {
                          newImages.push('');
                          setSocialProof({...socialProof, avatar_images: newImages});
                        }
                      }}
                      disabled={(socialProof.avatar_images || []).length >= 5}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Avatar
                    </Button>
                  </div>
                  
                  {(socialProof.avatar_images || []).length === 0 ? (
                    <p className="text-sm text-gray-500 italic">No custom avatars. Using default placeholder images.</p>
                  ) : (
                    <div className="space-y-3">
                      {socialProof.avatar_images.map((url, index) => (
                        <div key={index} className="flex items-center gap-3">
                          {url && (
                            <img 
                              src={url} 
                              alt="" 
                              className="w-10 h-10 rounded-full object-cover border"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                          <Input
                            value={url}
                            onChange={(e) => {
                              const newImages = [...socialProof.avatar_images];
                              newImages[index] = e.target.value;
                              setSocialProof({...socialProof, avatar_images: newImages});
                            }}
                            placeholder="Image URL"
                            className="flex-1"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const newImages = socialProof.avatar_images.filter((_, i) => i !== index);
                              setSocialProof({...socialProof, avatar_images: newImages});
                            }}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Preview */}
                <div className="mt-8 p-6 bg-ux-charcoal rounded-lg">
                  <Label className="text-white mb-4 block">Preview</Label>
                  <div className="flex items-center gap-6">
                    <div className="flex -space-x-3">
                      {((socialProof.avatar_images || []).length > 0 
                        ? socialProof.avatar_images.slice(0, 5)
                        : [1, 2, 3, 4, 5].map(i => `https://i.pravatar.cc/40?img=${i+10}`)
                      ).map((src, i) => (
                        <img 
                          key={i}
                          src={src || `https://i.pravatar.cc/40?img=${i+10}`}
                          alt=""
                          className="w-10 h-10 rounded-full border-2 border-white/50 object-cover"
                          onError={(e) => { e.target.src = `https://i.pravatar.cc/40?img=${i+10}`; }}
                        />
                      ))}
                    </div>
                    <div>
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <Star 
                            key={i} 
                            className={`h-4 w-4 ${i <= Math.round(socialProof.rating) ? 'fill-ux-amber text-ux-amber' : 'fill-gray-500 text-gray-500'}`}
                          />
                        ))}
                        <span className="ml-2 text-white font-semibold">{socialProof.rating}</span>
                      </div>
                      <p className="text-white/60 text-sm">{socialProof.total_reviews} {socialProof.review_text}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Navigation Settings Tab */}
          <TabsContent value="navigation">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Header Navigation</CardTitle>
                  <CardDescription>Edit the navigation menu links displayed in the website header</CardDescription>
                </div>
                <Button 
                  onClick={async () => {
                    setSaving(true);
                    try {
                      const res = await fetch(`${API_URL}/api/cms/site-settings/navigation`, {
                        method: 'PUT',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify(navigationSettings)
                      });
                      if (handleUnauthorized(res)) return;
                      if (res.ok) {
                        toast.success('Navigation settings saved!');
                      } else {
                        toast.error('Failed to save navigation settings');
                      }
                    } catch (error) {
                      toast.error('Error saving navigation');
                    } finally {
                      setSaving(false);
                    }
                  }} 
                  disabled={saving}
                >
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  <Save className="w-4 h-4 mr-2" />
                  Save Navigation
                </Button>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-sm text-slate-500">
                  Configure the dropdown menus and links shown in the header navigation. Each section can be expanded to add, remove, reorder, or toggle visibility of links.
                </p>
                
                {/* Destinations */}
                <FooterLinkSection
                  title="Destinations (with Flags)"
                  links={navigationSettings.destinations || []}
                  onUpdate={(links) => setNavigationSettings({ ...navigationSettings, destinations: links })}
                />
                
                {/* Services */}
                <FooterLinkSection
                  title="Services"
                  links={navigationSettings.services || []}
                  onUpdate={(links) => setNavigationSettings({ ...navigationSettings, services: links })}
                />
                
                {/* Student Services */}
                <FooterLinkSection
                  title="Student Services"
                  links={navigationSettings.student_services || []}
                  onUpdate={(links) => setNavigationSettings({ ...navigationSettings, student_services: links })}
                />
                
                {/* Popular Programs */}
                <FooterLinkSection
                  title="Popular Programs"
                  links={navigationSettings.programs || []}
                  onUpdate={(links) => setNavigationSettings({ ...navigationSettings, programs: links })}
                />
                
                {/* Comparisons */}
                <FooterLinkSection
                  title="Popular Comparisons"
                  links={navigationSettings.comparisons || []}
                  onUpdate={(links) => setNavigationSettings({ ...navigationSettings, comparisons: links })}
                />
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Journey Timeline Tab */}
          <TabsContent value="journey">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Journey Timeline / Personas</CardTitle>
                  <CardDescription>Edit the "What Drives You?" journey selector on the homepage</CardDescription>
                </div>
                <Button 
                  onClick={async () => {
                    setSaving(true);
                    try {
                      const res = await fetch(`${API_URL}/api/cms/site-settings/journey`, {
                        method: 'PUT',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify(journeySettings)
                      });
                      if (handleUnauthorized(res)) return;
                      if (res.ok) {
                        toast.success('Journey settings saved!');
                      } else {
                        toast.error('Failed to save journey settings');
                      }
                    } catch (error) {
                      toast.error('Error saving journey');
                    } finally {
                      setSaving(false);
                    }
                  }} 
                  disabled={saving}
                >
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  <Save className="w-4 h-4 mr-2" />
                  Save Journey
                </Button>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-500 mb-6">
                  The Journey section shows 4 persona cards (Achiever, Pragmatist, Value-Seeker, Explorer). Each persona has its own styling, steps, and call-to-action. Currently showing {journeySettings.personas?.length || 0} personas.
                </p>
                
                <div className="space-y-4">
                  {(journeySettings.personas || []).map((persona, idx) => (
                    <div key={persona.id || idx} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center bg-gradient-to-r ${persona.gradient || 'from-cyan-400 to-blue-500'}`}>
                            <Award className="w-4 h-4 text-white" />
                          </span>
                          <div>
                            <h4 className="font-medium">{persona.name}</h4>
                            <p className="text-xs text-slate-500">{persona.tagline}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              const updated = [...journeySettings.personas];
                              updated[idx].is_active = !updated[idx].is_active;
                              setJourneySettings({ ...journeySettings, personas: updated });
                            }}
                            className={`p-1.5 rounded ${persona.is_active !== false ? 'text-emerald-500' : 'text-slate-400'}`}
                          >
                            {persona.is_active !== false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <Label className="text-xs">CTA Button Text</Label>
                          <Input
                            value={persona.cta || ''}
                            onChange={(e) => {
                              const updated = [...journeySettings.personas];
                              updated[idx].cta = e.target.value;
                              setJourneySettings({ ...journeySettings, personas: updated });
                            }}
                            className="h-8 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Stats Label / Value</Label>
                          <div className="flex gap-1">
                            <Input
                              value={persona.stats_label || ''}
                              onChange={(e) => {
                                const updated = [...journeySettings.personas];
                                updated[idx].stats_label = e.target.value;
                                setJourneySettings({ ...journeySettings, personas: updated });
                              }}
                              placeholder="Label"
                              className="h-8 text-sm flex-1"
                            />
                            <Input
                              value={persona.stats_value || ''}
                              onChange={(e) => {
                                const updated = [...journeySettings.personas];
                                updated[idx].stats_value = e.target.value;
                                setJourneySettings({ ...journeySettings, personas: updated });
                              }}
                              placeholder="Value"
                              className="h-8 text-sm w-24"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {(!journeySettings.personas || journeySettings.personas.length === 0) && (
                  <div className="text-center py-8 text-slate-500">
                    <Plane className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No journey personas configured yet.</p>
                    <p className="text-sm">Default personas will be shown on the homepage.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Success Stories Tab */}
          <TabsContent value="success-stories">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Success Stories</CardTitle>
                  <CardDescription>Manage student success stories displayed on the Success Stories page</CardDescription>
                </div>
                <Button 
                  onClick={async () => {
                    setSaving(true);
                    try {
                      const res = await fetch(`${API_URL}/api/cms/site-settings/success-stories`, {
                        method: 'PUT',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify(successStoriesSettings)
                      });
                      if (handleUnauthorized(res)) return;
                      if (res.ok) {
                        toast.success('Success stories saved!');
                      } else {
                        toast.error('Failed to save success stories');
                      }
                    } catch (error) {
                      toast.error('Error saving success stories');
                    } finally {
                      setSaving(false);
                    }
                  }} 
                  disabled={saving}
                >
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  <Save className="w-4 h-4 mr-2" />
                  Save Stories
                </Button>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-500 mb-6">
                  Currently showing {successStoriesSettings.stories?.length || 0} success stories. Each story includes student name, photo, university, program, quote, and scholarship details.
                </p>
                
                {/* Stories List */}
                <div className="space-y-3">
                  {(successStoriesSettings.stories || []).map((story, idx) => (
                    <div key={story.id || idx} className="border rounded-lg p-4 flex items-start gap-4">
                      <img 
                        src={story.image} 
                        alt={story.name}
                        className="w-16 h-16 rounded-full object-cover"
                        onError={(e) => { e.target.src = 'https://via.placeholder.com/64'; }}
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium">{story.name}</h4>
                            <p className="text-sm text-slate-500">
                              {story.program} • {story.university_short || story.universityShort}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{story.country} {story.flag}</Badge>
                            <button
                              type="button"
                              onClick={() => {
                                const updated = [...successStoriesSettings.stories];
                                updated[idx].is_active = !updated[idx].is_active;
                                setSuccessStoriesSettings({ ...successStoriesSettings, stories: updated });
                              }}
                              className={`p-1.5 rounded ${story.is_active !== false ? 'text-emerald-500' : 'text-slate-400'}`}
                            >
                              {story.is_active !== false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const updated = successStoriesSettings.stories.filter((_, i) => i !== idx);
                                setSuccessStoriesSettings({ ...successStoriesSettings, stories: updated });
                              }}
                              className="p-1.5 text-red-400 hover:text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <p className="text-xs text-slate-400 mt-1 line-clamp-2">"{story.quote}"</p>
                        {story.scholarship && (
                          <Badge className="mt-2 bg-emerald-100 text-emerald-700">{story.scholarship}</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                
                {(!successStoriesSettings.stories || successStoriesSettings.stories.length === 0) && (
                  <div className="text-center py-8 text-slate-500">
                    <Award className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No success stories configured yet.</p>
                    <p className="text-sm">Default stories will be shown on the page.</p>
                  </div>
                )}
                
                {/* Stats Section */}
                <div className="mt-8 border-t pt-6">
                  <h4 className="font-medium mb-4">Page Statistics</h4>
                  <div className="grid grid-cols-4 gap-3">
                    {(successStoriesSettings.stats || []).map((stat, idx) => (
                      <div key={idx} className="space-y-1">
                        <Input
                          value={stat.value}
                          onChange={(e) => {
                            const updated = [...successStoriesSettings.stats];
                            updated[idx].value = e.target.value;
                            setSuccessStoriesSettings({ ...successStoriesSettings, stats: updated });
                          }}
                          className="h-8 text-sm font-bold"
                        />
                        <Input
                          value={stat.label}
                          onChange={(e) => {
                            const updated = [...successStoriesSettings.stats];
                            updated[idx].label = e.target.value;
                            setSuccessStoriesSettings({ ...successStoriesSettings, stats: updated });
                          }}
                          className="h-8 text-xs"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
      
      {/* Asset Picker Dialog for Testimonial Images */}
      <Dialog open={assetPickerOpen} onOpenChange={setAssetPickerOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[80vh]" aria-describedby="asset-picker-description">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Image className="w-5 h-5 text-blue-600" />
              Select Image from Assets
            </DialogTitle>
          </DialogHeader>
          
          <p id="asset-picker-description" className="text-sm text-slate-500 -mt-2">
            Choose an image from your CMS assets library to use as the testimonial photo.
          </p>
          
          <div className="py-4">
            {assetsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : availableAssets.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Image className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No images found in Assets Manager</p>
                <p className="text-sm mt-1">Upload images first in the Assets Manager</p>
              </div>
            ) : (
              <div 
                className="max-h-[450px] overflow-y-auto pr-2"
                style={{ 
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '12px',
                  padding: '4px'
                }}
              >
                {availableAssets.map((asset) => (
                  <button
                    key={asset.asset_id || asset.asset_key || Math.random()}
                    type="button"
                    onClick={() => handleAssetSelect(asset.url)}
                    className="group relative rounded-lg overflow-hidden border-2 border-slate-200 hover:border-blue-500 hover:shadow-lg transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 bg-slate-100"
                    style={{
                      aspectRatio: '1/1',
                      minHeight: '120px'
                    }}
                    data-testid={`asset-option-${asset.asset_key}`}
                  >
                    <img 
                      src={asset.url} 
                      alt={asset.name || 'Asset'}
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
                      <span className="text-white text-sm font-medium px-3 py-1.5 bg-blue-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
                        Select
                      </span>
                    </div>
                    {/* Name label */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-2 pt-6">
                      <p className="text-white text-xs font-medium truncate drop-shadow-sm">
                        {asset.name || 'Unnamed'}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssetPickerOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </AdminLayout>
  );
};

export default SiteSettingsManager;
